// Canonical prospect identity for the Prospecting workbook.
//
// This module is deliberately isomorphic: the browser imports it to derive the
// "Also in Leads" membership labels, and the Netlify sync function imports it to
// decide which source rows are missing from the Leads master list. Both sides
// must agree, otherwise reconciliation would append a row the UI already treats
// as the same person.

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi

// Hosts that identify a social/profile page rather than a prospect's own site.
// A bare domain match on these would merge unrelated prospects, so they only
// ever contribute a full-path profile key.
const SHARED_HOSTS = new Set([
  'facebook.com', 'fb.com', 'fb.me', 'messenger.com', 'instagram.com',
  'linkedin.com', 'tiktok.com', 'twitter.com', 'x.com', 'youtube.com',
  'youtu.be', 'threads.net', 'snapchat.com', 'pinterest.com', 'reddit.com',
  'nextdoor.com', 'yelp.com', 'google.com', 'g.co', 'goo.gl', 'maps.app.goo.gl',
  'sites.google.com', 'business.site', 'linktr.ee', 'wixsite.com', 'wix.com',
  'squarespace.com', 'godaddysites.com', 'weebly.com', 'wordpress.com',
  'angi.com', 'thumbtack.com', 'homeadvisor.com', 'bbb.org',
])

const HOST_PREFIXES = /^(?:www|m|web|mobile|business|en-gb|en)\./i

export function cleanCell(value) {
  return String(value ?? '').replace(/\r/g, '').replace(/\s+/g, ' ').trim()
}

export function isTruthyCell(value) {
  return ['true', 'yes', '1', 'y', 'x'].includes(cleanCell(value).toLowerCase())
}

export function normalizeEmails(value) {
  const matches = String(value ?? '').match(EMAIL_PATTERN) ?? []
  return Array.from(new Set(matches.map((email) => email.toLowerCase().replace(/\.$/, ''))))
}

// Digits only, with the North American country code dropped so that
// "+1 (555) 010-2020" and "555.010.2020" resolve to the same prospect.
export function normalizePhone(value) {
  let digits = String(value ?? '').replace(/\D+/g, '')
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1)
  return digits.length >= 10 ? digits : ''
}

function candidateUrls(value) {
  const raw = cleanCell(value)
  if (!raw) return []
  const explicit = raw.match(/https?:\/\/[^\s,;|]+/gi)
  if (explicit?.length) return explicit
  return raw
    .split(/\s*(?:,|;|\||\bor\b|\band\b)\s*/i)
    .map((part) => part.trim())
    .filter((part) => /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}(?:[/?#].*)?$/i.test(part))
}

function parseUrl(candidate) {
  const withProtocol = /^https?:\/\//i.test(candidate) ? candidate : `https://${candidate}`
  try {
    return new URL(withProtocol)
  } catch {
    return null
  }
}

export function normalizeHost(url) {
  return url.hostname.toLowerCase().replace(HOST_PREFIXES, '')
}

function normalizePath(url) {
  return url.pathname.replace(/\/+$/, '').toLowerCase()
}

// Facebook exposes the same person as /profile.php?id=123 and as /vanity.name,
// so the numeric id is kept when present and every other query string dropped.
function profileKeyFor(url) {
  const host = normalizeHost(url)
  const path = normalizePath(url)
  if (/\/profile\.php$/.test(path)) {
    const id = cleanCell(url.searchParams.get('id')).toLowerCase()
    return id ? `${host}/profile.php?id=${id}` : ''
  }
  if (!path || path === '/') return ''
  return `${host}${path}`
}

export function normalizeName(value) {
  return cleanCell(value).toLowerCase().replace(/[’‘]/g, "'").replace(/[^a-z0-9]+/g, ' ').trim()
}

// Strong identifiers: anything that pins a real-world identity on its own.
// Order is stable so the canonical group id below is deterministic.
export function strongIdentityKeys(record) {
  const keys = new Set()

  for (const email of normalizeEmails(record?.email)) keys.add(`email:${email}`)

  const phone = normalizePhone(record?.phone)
  if (phone) keys.add(`phone:${phone}`)

  for (const field of [record?.profile, record?.website]) {
    for (const candidate of candidateUrls(field)) {
      const url = parseUrl(candidate)
      if (!url) continue
      const host = normalizeHost(url)
      if (!host || !host.includes('.')) continue
      if (SHARED_HOSTS.has(host)) {
        const profile = profileKeyFor(url)
        if (profile) keys.add(`profile:${profile}`)
        continue
      }
      keys.add(`site:${host}`)
    }
  }

  return Array.from(keys).sort()
}

// The person/business name is only trusted when nothing stronger exists.
export function fallbackIdentityKeys(record) {
  const name = normalizeName(record?.person)
  return name ? [`name:${name}`] : []
}

export function identityKeysFor(record) {
  const strong = strongIdentityKeys(record)
  return strong.length ? strong : fallbackIdentityKeys(record)
}

function createDisjointSet() {
  const parent = new Map()

  function find(key) {
    if (!parent.has(key)) parent.set(key, key)
    let root = key
    while (parent.get(root) !== root) root = parent.get(root)
    let cursor = key
    while (parent.get(cursor) !== root) {
      const next = parent.get(cursor)
      parent.set(cursor, root)
      cursor = next
    }
    return root
  }

  return {
    find,
    union(a, b) {
      const rootA = find(a)
      const rootB = find(b)
      if (rootA !== rootB) parent.set(rootB, rootA)
    },
    keys() {
      return Array.from(parent.keys())
    },
  }
}

// Two records belong to the same prospect when they share any strong key, and
// that relation is transitive: a shared phone plus a shared website collapses
// three rows into one identity. The canonical id is the smallest key in the
// component so it stays stable no matter what order the sheets are read in.
export function buildIdentityGroups(records) {
  const set = createDisjointSet()
  const recordKeys = records.map((record) => identityKeysFor(record))

  for (const keys of recordKeys) {
    if (!keys.length) continue
    for (const key of keys) set.union(keys[0], key)
  }

  const canonical = new Map()
  for (const key of set.keys()) {
    const root = set.find(key)
    const current = canonical.get(root)
    if (current === undefined || key < current) canonical.set(root, key)
  }

  return records.map((record, index) => {
    const keys = recordKeys[index]
    const groupId = keys.length ? canonical.get(set.find(keys[0])) : ''
    return { record, keys, groupId: groupId ?? '' }
  })
}
